import * as React from 'react';
import Button from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';
import { Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import { localizations } from '../localization/localization';
import Add from '@material-ui/icons/Add';
import { connect } from 'react-redux';
import { ApplicationState } from '../store';
import { RowInfo } from "react-table";
import { UnitModel } from './UnitModels';
import * as moment from 'moment';
import * as UnitStore from './store/unitStore'
import ViewHeaderBar from '../components/ViewHeaderBar';
import FreeUnitDetails from './FreeUnitDetails';
import { momentToString, dateStringSort } from '../utils/dateUtilities';
import AddNewUnitForm from './forms/AddNewUnitForm';
import customTheme from '../common/theme';
import { withExpander, withHeaderStyle } from '../components/tables/TableUtils';
import DeleteUnitDialog from './forms/DeleteUnitDialog';
import UnitEmailDialog from './forms/UnitEmailDialog';
import * as LoginStore from '../login/loginStore'
import * as FsStore from '../account/store/FilterSettingsStore';
import { FilterInfo } from '../common/models/CommonModels';
import Own from '@material-ui/icons/Person'
import Company from '@material-ui/icons/People'
import Deleted from '@material-ui/icons/Delete';
import { push } from 'react-router-redux';
import { bindActionCreators } from 'redux';
import { RouteName } from '../routeNames';
import FilteredTableWithPaging from '../components/tables/FilteredTableWithPaging';
import { toCommaDecimal } from '../utils/numberUtils';
import { RecurrenceType } from '../common/models/Recurrence';
import { UserRole } from '../login/LoginModels';

const styles = (theme: Theme) => ({
    leftIcon: {
        marginRight: theme.spacing(1),
    },
});

type WithStylesProps = "leftIcon";
type FreeUnitsViewProps = WithStyles<WithStylesProps>
    & { filterSettingsActions: typeof FsStore.actionsCreators, unitActions: typeof UnitStore.actionCreators, goToActions: typeof goActions } 
    & { filterSettings: FsStore.FilterSettingsState, units: UnitStore.FreeUnitState, login: LoginStore.LoginState };

const columns = withHeaderStyle(customTheme)(withExpander([
    {
        Header: "",
        id: "isOwn",
        accessor: (u: UnitModel) => {
            let value = "";

            if (u.isOwn) {
                value = "1"
            }
            else if (u.isOwnCompany) {
                value = "2"
            }
            else {
                value = "3"
            }

            if (u.deletedAt) {
                value += "d";
            }

            return value;
        },
        Cell: row => {
            const u = row.original as UnitModel;

            return (
                <span>
                    {u.isOwn ? <Tooltip title={localizations.company.personalAd}><span><Own color={u.deletedAt ? "disabled" : "secondary"} /></span></Tooltip> : u.isOwnCompany ? <Tooltip title= {localizations.common.companyAd}><span><Company color={u.deletedAt ? "disabled" : "secondary"} /></span></Tooltip> : null}
                    {u.deletedAt ? <Tooltip title={ localizations.common.deletedAt + " " + momentToString(u.deletedAt)} ><span><Deleted color="disabled" /></span></Tooltip> : null}
                </span>
            );
        },
        width: 60,
        sortable: true
    },
    {
        Header: localizations.common.reportNumberCol,
        accessor: "reportNumber",
        maxWidth: 100
    },
    {
        Header: `${localizations.units.readyForDelivery}`,
        id: "readyForDelivery",
        accessor: (d: UnitModel) => momentToString(d.readyTime),
        sortMethod: dateStringSort,
        Cell: row => {
            const unit = row.original as UnitModel;
            if (unit) {
                if (unit.deletedAt) {
                    return <span style={{ color: "gray" }}>{row.value}</span>
                }

                const now = moment();

                if (unit.readyTime < now) {
                    return <span style={{ color: "red" }}>{row.value}</span>
                }
            }
            return <span>{row.value}</span>
        },
    },
    {
        Header: `${localizations.units.timeOfDelivery}`,
        id: "timeOfDelivery",
        accessor: (d: UnitModel) => momentToString(d.deliveryTime),
        sortMethod: dateStringSort,
        Cell: row => {
            const unit = row.original as UnitModel;
            const now = moment();

            if (unit) {
                if (unit.deletedAt) {
                    return <span style={{ color: "gray" }}>{row.value}</span>
                }

                if (unit.deliveryTime < now) {
                    return <span style={{ color: "red" }}>{row.value}</span>
                }
            }
            
            const currDate = now.format('DD-MM-YYYY');
            const deliDate = unit.deliveryTime.format('DD-MM-YYYY');
            if (currDate === deliDate) {
                const timeDiff = unit.deliveryTime.diff(now);
                if (timeDiff > 0) {
                    // duration in hours
                    const hours = unit.deliveryTime.diff(now, 'hours');

                    // duration in minutes
                    const minutes = unit.deliveryTime.diff(now, 'minutes')%60;
                    const minutesStr = ("0"+minutes).slice(-2);

                    if (hours < 1) {
                        return <span style={{ color: "red" }}>{row.value}, {localizations.common.timeLeft} {minutes}{localizations.common.min}</span>
                    }
                    return <span style={{ color: "red" }}>{row.value}, {localizations.common.timeLeft} {hours}{localizations.common.h} {minutesStr}{localizations.common.min}</span>
                }

                return <span style={{ color: "red" }}>{row.value}</span>
            }
            return <span>{row.value}</span>
        },
    },
    {
        Header: `${localizations.common.pointOfDeparture}`,
        id: "departureCity",
        accessor: (d: UnitModel) => d.departureCity ? d.departureCity.name : "",
        Cell: row => {
            const unit = row.original as UnitModel;
            if (unit) {
                if (unit.deletedAt) {
                    return <span style={{ color: "gray" }}>{row.value}</span>
                }
            }
            return <span>{row.value}</span>
        },
    },
    {
        Header: `${localizations.common.pointOfArrival}`,
        id: "destinationCity",
        accessor: (d: UnitModel) => d.destinationCity ? d.destinationCity.name : "",
        Cell: row => {
            const unit = row.original as UnitModel;
            if (unit) {
                if (unit.deletedAt) {
                    return <span style={{ color: "gray" }}>{row.value}</span>
                }
            }
            return <span>{row.value}</span>
        },
    },
    {
        Header: `${localizations.common.type}`,
        id: "unitType",
        accessor: (d: UnitModel) => `${localizations.unitTypes[d.trailerType]}`,
        Cell: row => {
            const unit = row.original as UnitModel;
            if (unit) {
                if (unit.deletedAt) {
                    return <span style={{ color: "gray" }}>{row.value}</span>
                }
            }
            return <span>{row.value}</span>
        },
    },
    {
        Header: localizations.common.contractPriceCol,
        id: "contractPrice",
        accessor: (u: UnitModel) => u.contractPrice ? `${toCommaDecimal(u.contractPrice)} €` : undefined
    }
]));

interface UnitsViewState {
    refreshSwitch: boolean;
}

const UnitTable = FilteredTableWithPaging as new () => FilteredTableWithPaging<UnitModel>;

class FreeUnitsView extends React.Component<FreeUnitsViewProps, UnitsViewState> {
    constructor(props: FreeUnitsViewProps) {
        super(props);

        this.state = {
            refreshSwitch: false
        }

        this.toSubComponent = this.toSubComponent.bind(this);
        this.handleEdit = this.handleEdit.bind(this);
        this.handleCopy = this.handleCopy.bind(this);
        this.handleDelete = this.handleDelete.bind(this);
        this.isOwnCompany = this.isOwnCompany.bind(this);
        this.isEditEnabled = this.isEditEnabled.bind(this);
        this.isDeleteEnabled = this.isDeleteEnabled.bind(this);
        this.isCopyEnabled = this.isCopyEnabled.bind(this);
        this.handleFetch = this.handleFetch.bind(this);
        this.handleGoToMap = this.handleGoToMap.bind(this);
        this.handleAdd = this.handleAdd.bind(this);
        this.afterEdit = this.afterEdit.bind(this);
        this.handleSendEmail = this.handleSendEmail.bind(this);
        this.handleSendEmailToReporter = this.handleSendEmailToReporter.bind(this);
    }

    public render() {
        const { classes } = this.props;
        const adding = this.props.units.isEditing;
        const isEditMode = adding && this.props.units.editedItem;

        const initialSearchFilter = (this.props.units.selected && this.props.units.selected.reportNumber) ? this.props.units.selected.reportNumber : "";

        const isDialogOpen = this.props.units.isSendingEmail || this.props.units.isSendingEmailToReporter;

        return (
            <div>
                <ViewHeaderBar title={localizations.menus.freeUnits} showHelp helpAnchor="unitsview">
                    <Button variant="contained" color="secondary" onClick={() => this.handleAdd()}>
                        <Add className={classes.leftIcon} />{localizations.actions.addNew}
                    </Button>
                </ViewHeaderBar>
                <UnitTable
                    onFetchData={this.handleFetch}
                    columns={columns}
                    toSubComponent={this.toSubComponent}
                    filterIncludes={{ trucks: false, units: true }}
                    selected={null}
                    initialFilterValues={{
                        reportNumberSearch: initialSearchFilter
                    }}
                    defaultSortedColumns={[
                        { id: "readyForDelivery", desc: true }
                    ]}
                    refreshSwitch={this.state.refreshSwitch}
                    {...this.props.units}
                    onSaveFilter={this.props.filterSettingsActions.AddOrEditFilterSetting}
                    onSaveFilterCancel={this.props.filterSettingsActions.addOrEditFormCancelled}
                    onSaveFilterSubmit={this.props.filterSettingsActions.addOrEditFormSubmitted}
                    updateSaveFilterDestinationCities={this.props.filterSettingsActions.updateNewFilterSettingsDestinationCities}
                    updateSaveFilterDepartureCities={this.props.filterSettingsActions.updateNewFilterSettingsDepartureCities}
                    updateSaveFilterReporters={this.props.filterSettingsActions.updateNewFilterSettingsReporters}
                    filterSettings={this.props.filterSettings}
                />
                {adding ? (
                    <AddNewUnitForm
                        isOpen={adding}
                        isEditMode={isEditMode}
                        editMode={this.props.units.editMode}
                        onCancel={this.props.unitActions.addOrEditFormCancelled}
                        onSubmit={this.props.unitActions.addOrEditFormSubmitted}
                        resetTrailerIdentifier={this.props.unitActions.ResetAddNewUnitFormTrailerIdentifier}
                    />
                ) : null}
                {this.props.units.toDeleteItem ? (
                    <DeleteUnitDialog
                        isOpen={this.props.units.isDeleting}
                        onSubmit={this.props.unitActions.deleteSubmitted}
                        onCancel={this.props.unitActions.deleteCancelled}
                        unit={this.props.units.toDeleteItem}
                        companyId={this.props.units.toDeleteItem.companyId}
                    />
                ) : null}
                {isDialogOpen ? (
                    <UnitEmailDialog
                        isOpen={isDialogOpen}
                        onSubmit={this.props.units.isSendingEmail ? this.props.unitActions.sendEmailSubmitted : this.props.unitActions.sendEmailToReporterSubmitted}
                        onCancel={this.props.units.isSendingEmail ? this.props.unitActions.sendEmailCancelled : this.props.unitActions.sendEmailToReporterCancelled}
                        toReporter={this.props.units.isSendingEmailToReporter}
                    />
                ) : null}
            </div>
        );
    }

    private refreshFilters() {
        this.setState((prevState) => {
            return {
                refreshSwitch: !prevState.refreshSwitch
            }
        });
    }

    private handleAdd() {
        this.props.unitActions.addOrEditUnit(undefined, this.afterEdit, true);
    }

    private afterEdit() {
        this.refreshFilters();
    }

    private handleFetch(filters: FilterInfo) {
        this.props.unitActions.cacheFilters(filters);
        this.props.unitActions.requestFreeUnits(filters, true);

        // Reset initial search params if given.
        this.props.unitActions.selectUnit(null);
    }

    private toSubComponent(row: RowInfo) {
        return (
            <div id={row.original.id}>
                <FreeUnitDetails
                    isEditEnabled={this.isEditEnabled}
                    onEdit={this.handleEdit}
                    isDeleteEnabled={this.isDeleteEnabled}
                    onDelete={this.props.unitActions.deleteUnit}
                    isCopyEnabled={this.isCopyEnabled}
                    onCopy={this.handleCopy}
                    onGoToMap={this.handleGoToMap}
                    onSendEmail={this.handleSendEmail}
                    onSendEmailToReporter={this.handleSendEmailToReporter}
                    {...row.original}
                />
            </div>
        );
    }

    private handleGoToMap(unit: UnitModel) {
        this.props.unitActions.selectUnit(unit);
        this.props.goToActions.goTo(RouteName.Map);
    }

    private handleEdit(unit: UnitModel) {
        const unitData = {
            ...unit,
            details: unit.details ? unit.details : "",
            noDestination: !unit.destinationCity,
            recurrence: {
                ...unit.recurrence,
                weekdays: unit.recurrence.weekdays ? unit.recurrence.weekdays : {
                    monday: false,
                    tuesday: false,
                    wednesday: false,
                    thursday: false,
                    friday: false,
                    saturday: false,
                    sunday: false
                },
                everyNthOccurrence: unit.recurrence.everyNthOccurrence ? unit.recurrence.everyNthOccurrence : 1,
                maxOccurrences: unit.recurrence.maxOccurrences ? unit.recurrence.maxOccurrences : 1,
                type: "weekly" as RecurrenceType,
                startDate: unit.recurrence.startDate ? unit.recurrence.startDate : unit.readyTime,
                endDate: unit.recurrence.endDate ? unit.recurrence.endDate : unit.readyTime.clone().add(1, 'days')
            }
        };
        this.props.unitActions.addOrEditUnit(unitData, this.afterEdit, true);
    }

    private isOwnCompany(unit: UnitModel) {
        return unit.company.id === this.props.login.companyId;
    }

    private handleDelete(unit: UnitModel) {
        this.props.unitActions.deleteUnit(unit, this.afterEdit);
    }

    private handleCopy(unit: UnitModel) {
        const unitData = {
            ...unit,
            details: unit.details ? unit.details : "",
            noDestination: !unit.destinationCity,
            recurrence: {
                ...unit.recurrence,
                weekdays: unit.recurrence.weekdays ? unit.recurrence.weekdays : {
                    monday: false,
                    tuesday: false,
                    wednesday: false,
                    thursday: false,
                    friday: false,
                    saturday: false,
                    sunday: false
                },
                everyNthOccurrence: unit.recurrence.everyNthOccurrence ? unit.recurrence.everyNthOccurrence : 1,
                maxOccurrences: unit.recurrence.maxOccurrences ? unit.recurrence.maxOccurrences : 1,
                type: "weekly" as RecurrenceType,
                startDate: unit.recurrence.startDate ? unit.recurrence.startDate : unit.readyTime,
                endDate: unit.recurrence.endDate ? unit.recurrence.endDate : unit.readyTime.clone().add(1, 'days')
            }
        };
        this.props.unitActions.copyUnit(unitData);
    }

    private isEditEnabled(unit: UnitModel) {
        return (this.isOwnCompany(unit) || this.props.login.role === UserRole.Admin) && !unit.deletedAt;
    }

    private isDeleteEnabled(unit: UnitModel) {
        return (this.isOwnCompany(unit) || this.props.login.role === UserRole.Admin) && !unit.deletedAt;
    }

    private isCopyEnabled(unit: UnitModel) {
        return (this.isOwnCompany(unit) || this.props.login.role === UserRole.Admin) && !unit.deletedAt;
    }
    
    private handleSendEmail(unit: UnitModel) {
        this.props.unitActions.sendEmail(unit);
    }
    
    private handleSendEmailToReporter(unit: UnitModel) {
        this.props.unitActions.sendEmailToReporter(unit);
    }
}

const mapStateToProps = (state: ApplicationState) => {
    const { units, login, filterSettings } = state;
    return { units, login, filterSettings };
}

const goActions = {
    goTo: (location: string) => push(location)
}

const mapDispatchToProps = (dispatch: any) => {
    return {
        filterSettingsActions: bindActionCreators(FsStore.actionsCreators, dispatch),
        unitActions: bindActionCreators(UnitStore.actionCreators, dispatch),
        goToActions: bindActionCreators(goActions, dispatch)
    }
}

export default withStyles(styles)(connect<any, any, any>(mapStateToProps, mapDispatchToProps)(FreeUnitsView));



// WEBPACK FOOTER //
// ./src/units/FreeUnitsView.tsx